import React, { FC, useCallback, useContext } from 'react';
import { composeFetcher, createFetcherWrapper } from '../../contexts/Fetcher';
import { LiveStack } from '../CloudLive/LiveStack';
import { LiveRegionContext, LiveRegionContextProvider } from '../../contexts/LiveRegionContext';
import { LiveRegionSelect } from '../LiveRegionSelect/LiveRegionSelect';
import { LiveStackStatusTransitional } from '../../hooks/useLiveApi';
import { LiveRegionsFetcher } from '../../fetchers/LiveRegionsFetcher';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useLoopedInterval } from '../../hooks/useLoopedInterval';
import { LivePreviewFetcher } from '../CloudLive/CloudLive';
import { apiCloudLive } from '../../resources/api/apiCloudLive';
import { OrganizationContextProviderLazy } from '../../contexts/OrganizationContextProvider';

const { useGetAllStacksList, useGetAllStacksListSilent } = apiCloudLive;

const AllLiveStacksFetcherWrapper = createFetcherWrapper(LivePreviewFetcher.Context, () => {
  const { region } = useContext(LiveRegionContext);
  const { execute } = useGetAllStacksList(region);
  const { execute: executeSilent } = useGetAllStacksListSilent(region);

  return useCallback(
    async ({ silent = false }) => (region ? (silent ? executeSilent : execute)() : []),
    [execute, executeSilent, region]
  );
});

const AllLiveStacksFetcher = composeFetcher(LivePreviewFetcher.Context, AllLiveStacksFetcherWrapper);

// Refresh once every 5 minutes when all stack are stable
const REFRESH_TIMEOUT_IDLE = 5 * 60 * 1000;
// Refresh once every 10 seconds when any stack is in transitional state
const REFRESH_TIMEOUT_TRANSITIONAL = 10 * 1000;

const AllLiveStacksContent: FC = () => {
  const { data, onRefresh } = useContext(AllLiveStacksFetcher.Context);

  const isAnyTransitionalState = (data ?? []).some((stack) => LiveStackStatusTransitional.includes(stack.status));

  const refreshTimeout = isAnyTransitionalState ? REFRESH_TIMEOUT_TRANSITIONAL : REFRESH_TIMEOUT_IDLE;

  useLoopedInterval({
    interval: refreshTimeout,
    callback: useCallback(() => onRefresh({ silent: true }), [onRefresh]),
  });

  if (!data) return null;

  return (
    <Grid container spacing={1}>
      {data.map((stack) => {
        return (
          <Grid item xs={12} md={6} lg={4} key={stack.stackName}>
            {stack.organizationUuid ? (
              <OrganizationContextProviderLazy id={stack.organizationUuid}>
                <LiveStack stack={stack} onRefresh={onRefresh} showOrganization={true} showUser={true} />
              </OrganizationContextProviderLazy>
            ) : (
              <LiveStack stack={stack} onRefresh={onRefresh} />
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

export const AllLiveStacks = () => {
  return (
    <LiveRegionsFetcher.WAF>
      <LiveRegionContextProvider>
        <LiveRegionSelect />
        <Box mt={1}>
          <AllLiveStacksFetcher.WAF>
            <AllLiveStacksContent />
          </AllLiveStacksFetcher.WAF>
        </Box>
      </LiveRegionContextProvider>
    </LiveRegionsFetcher.WAF>
  );
};
